import type { AudioFile, Concert } from "../../static";
import type { Action } from "svelte/action";
import AudioMotionAnalyzer from "audiomotion-analyzer";

const defaultFadeInDuration = 0.125;
const defaultFadeOutDuration = 0.5;

interface ActiveSound {
    source: AudioBufferSourceNode;
    gain: GainNode;
    fade: GainNode;
}

export class Audio {
    public analyserNode: AnalyserNode;
    public globalGainNode: GainNode;

    private audioContext = new AudioContext();
    private cache: Record<string, { file: AudioFile; buffer: AudioBuffer }> = {};
    private active: Record<string, ActiveSound> = {};

    constructor() {
        this.analyserNode = this.audioContext.createAnalyser();
        this.globalGainNode = this.audioContext.createGain();
        this.globalGainNode.gain.value = 1;
        this.globalGainNode.connect(this.analyserNode).connect(this.audioContext.destination);

        if (this.audioContext.state == "suspended") {
            console.log("Audio context is suspended, waiting for interaction...");

            const handler = async () => {
                console.log("Interaction occurred, resuming audio context");

                // Call to 'resume' must occur in interaction event handler
                this.audioContext.resume();

                try {
                    console.log("Requesting wake lock...");
                    await navigator.wakeLock.request("screen");
                    console.log("Wake lock acquired");
                } catch (e) {
                    console.error("Error requesting wake lock:", e);
                }

                document.removeEventListener("click", handler);
            };

            document.addEventListener("click", handler);
        }
    }

    get isReady() {
        return this.audioContext.state === "running";
    }

    async preload(concert: Concert, onprogress?: (file: number, total: number) => void) {
        const start = performance.now();

        let downloaded = 0;
        await Promise.all(
            concert.audioFiles.map(async (audioFile) => {
                const response = await fetch(import.meta.env.VITE_STATIC_URL + audioFile.url, {
                    cache: "force-cache",
                });
                const data = await response.arrayBuffer();
                const audioBuffer = await this.audioContext.decodeAudioData(data);

                this.cache[audioFile.name] = { file: audioFile, buffer: audioBuffer };

                downloaded += 1;
                onprogress?.(downloaded, concert.audioFiles.length);
            }),
        );

        console.log(`preloading sounds took ${performance.now() - start}ms`);
    }

    playSound(name: string, volume: number) {
        console.log(`Playing ${name} at volume ${volume}`);

        const cached = this.cache[name];
        if (!cached) {
            console.error("Attempted to play sound that is not loaded");
            return;
        }

        const { file, buffer } = cached;

        const activeSound = (this.active[name] ??= this.initializeActiveSound(file, buffer));
        this.setActiveSoundVolume(activeSound, volume);
    }

    stopSound(name: string) {
        console.log(`Stopping ${name}`);

        const activeSound = this.active[name];
        if (!activeSound) return;

        delete this.active[name];
        this.deinitializeActiveSound(activeSound);
    }

    stopAll() {
        console.log("Stopping all sounds");

        for (const [name, activeSound] of Object.entries(this.active)) {
            delete this.active[name];
            this.deinitializeActiveSound(activeSound);
        }
    }

    setGlobalVolume(globalVolume: number) {
        console.log("Setting global volume:", globalVolume);
        this.globalGainNode.gain.setValueAtTime(globalVolume, this.audioContext.currentTime);
    }

    private initializeActiveSound(file: AudioFile, buffer: AudioBuffer) {
        let startTime: number;
        switch (file.playbackMode) {
            case "fromStart":
                startTime = 0;
                break;
            case "random":
                startTime = Math.random() * buffer.duration;
                break;
            default:
                throw new Error("unknown playback mode");
        }

        const bufferSource = this.audioContext.createBufferSource();
        bufferSource.buffer = buffer;

        const gainNode = this.audioContext.createGain();

        const fadeNode = this.audioContext.createGain();
        fadeNode.gain.setValueAtTime(0, this.audioContext.currentTime);
        fadeNode.gain.linearRampToValueAtTime(
            1,
            this.audioContext.currentTime + (file.fadeInDuration ?? defaultFadeInDuration),
        );

        if (file.loops) {
            bufferSource.loop = true;
            bufferSource.loopStart = 0;
            bufferSource.loopEnd = buffer.duration;
        } else {
            fadeNode.gain.linearRampToValueAtTime(
                0,
                this.audioContext.currentTime + buffer.duration - defaultFadeOutDuration,
            );
        }

        bufferSource.connect(gainNode).connect(fadeNode).connect(this.globalGainNode);

        bufferSource.start(this.audioContext.currentTime, startTime);

        return {
            source: bufferSource,
            gain: gainNode,
            fade: fadeNode,
        };
    }

    private setActiveSoundVolume(activeSound: ActiveSound, volume: number) {
        activeSound.gain.gain.setValueAtTime(volume, this.audioContext.currentTime);
    }

    private deinitializeActiveSound(activeSound: ActiveSound) {
        const { source, gain } = activeSound;

        gain.gain.setValueAtTime(gain.gain.value, this.audioContext.currentTime);

        if (gain.gain.cancelAndHoldAtTime) {
            gain.gain.cancelAndHoldAtTime(this.audioContext.currentTime);
        } else {
            gain.gain.cancelScheduledValues(this.audioContext.currentTime);
        }

        gain.gain.linearRampToValueAtTime(
            0,
            this.audioContext.currentTime + defaultFadeOutDuration,
        );

        setTimeout(() => {
            gain.disconnect();
            source.stop();
            source.disconnect();
        }, defaultFadeOutDuration * 1000);
    }
}

export const audioMotion: Action<
    HTMLDivElement,
    { audio: Audio; radius?: number; color: string }
> = (node, { audio, radius = 0.5, color }) => {
    const analyzer = new AudioMotionAnalyzer(node, {
        source: audio.analyserNode,
        connectSpeakers: false,
        mode: 10,
        frequencyScale: "bark",
        minFreq: 200,
        maxFreq: 2000,
        radial: true,
        radialInvert: false,
        radius,
        mirror: 1,
        showScaleX: false,
        showScaleY: false,
        showPeaks: false,
        spinSpeed: 1,
        overlay: true,
        showBgColor: false,
        outlineBars: true,
        onCanvasResize: (_reason, analyzer) => {
            analyzer.canvas.getContext("2d")!.filter = "blur(6px)";
        },
    });

    analyzer.registerGradient("default", {
        bgColor: "black", // not shown because of 'overlay' and 'showBgColor'
        colorStops: [color],
    });

    analyzer.gradient = "default";

    analyzer.canvas.style.width = "100%";
    analyzer.canvas.style.height = "100%";

    analyzer.start();

    return {
        destroy: () => {
            analyzer.stop();
        },
    };
};
